/**
 * HowToPlayOnboarding — Build 4 first-login flow. Replaces the old
 * wall-of-text guide with a 3-screen visual carousel:
 *   1. Draft your roster   2. Real news = real scores   3. Compete & climb
 * Progress dots + arrows; final screen CTA = "Start Drafting".
 * The full rules page (HowToPlayAndRules) stays reachable from More → Library.
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const SLIDES = [
  {
    img: '/onboarding/draft.jpg',
    title: 'Draft Your Roster',
    body: 'Build a team of real-world power players — Rulers, CEOs, Athletes, Artists, Influencers & Icons. Each one is like a stock: their price (₣) rises and falls with their fame.',
  },
  {
    img: '/onboarding/scoring.jpg',
    title: 'The News Moves Everything',
    body: 'Headlines pay like dividends — when your people win, drop hits, or go viral, your team collects ₣. Frequent news raises a star\u2019s price. Quiet spells drag it down.',
  },
  {
    img: '/onboarding/compete.jpg',
    title: 'Compete & Climb',
    body: 'Buy low, sell high, and follow the news to spot who pops next. Your Net Worth (cash + roster value) ranks you on global and private leaderboards. Claim the podium.',
    note: '\uD83D\uDCA1 Side note: you start with free Boosters — one-shot power-ups (2× Captain weekends, category surges). Play them from Stage → Boosters.',
  },
];

export default function HowToPlayOnboarding({ onClose, onStartDrafting }) {
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="howtoplay-onboarding">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4">
        <span className="text-xs text-muted-foreground tabular-nums" data-testid="onboarding-progress-label">
          {idx + 1}/{SLIDES.length}
        </span>
        <button
          onClick={onClose}
          className="p-2 hover:bg-secondary rounded-lg text-muted-foreground"
          aria-label="Skip intro"
          data-testid="onboarding-skip"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Slide */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center max-w-md mx-auto w-full">
        <img
          src={slide.img}
          alt={slide.title}
          className="w-full max-w-[300px] aspect-square object-cover rounded-3xl border border-border/60 shadow-lg mb-6"
          data-testid={`onboarding-slide-image-${idx}`}
        />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }} data-testid="onboarding-slide-title">
          {slide.title}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed" data-testid="onboarding-slide-body">
          {slide.body}
        </p>
        {slide.note && (
          <p
            className="mt-3 text-[11px] text-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.08)] rounded-lg px-3 py-2"
            data-testid="onboarding-slide-note"
          >
            {slide.note}
          </p>
        )}
      </div>

      {/* Dots + arrows + CTA */}
      <div className="px-6 pb-8 max-w-md mx-auto w-full">
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="onboarding-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-primary' : 'w-2 bg-border'}`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className={`shrink-0 ${idx === 0 ? 'invisible' : ''}`}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            aria-label="Previous"
            data-testid="onboarding-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          {isLast ? (
            <Button
              className="flex-1 h-11 text-sm font-semibold"
              onClick={onStartDrafting || onClose}
              data-testid="onboarding-start-drafting"
            >
              <Star className="h-4 w-4 mr-2" /> Start Drafting
            </Button>
          ) : (
            <Button
              className="flex-1 h-11 text-sm font-semibold"
              onClick={() => setIdx((i) => Math.min(SLIDES.length - 1, i + 1))}
              data-testid="onboarding-next"
            >
              Next <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
